import { useState } from 'react';
import { Modal } from '@mantine/core';
import { Button } from './Button';
import { Icon } from './Icon';
import { useResolveConflict } from '@/hooks/useAdmin';

type Props = {
  opened: boolean;
  onClose: () => void;
  phone: string;
  entrepreneurName: string;
};

/**
 * Arbitration modal — the operator chooses MERGE or KEEP_SEPARATE for a
 * flagged conflict, supplies a reason, and the backend emits an
 * IDENTITY_MERGE event that flips the projection back to OK.
 *
 * For MERGE, the modal optionally lets the operator type the canonical
 * phone (only needed when two phones are tied to the same identity in
 * the upstream — the typical case for PDE.CI collisions).
 */
export function ResolveConflictModal({ opened, onClose, phone, entrepreneurName }: Props) {
  const resolve = useResolveConflict(phone);
  const [action, setAction] = useState<'MERGE' | 'KEEP_SEPARATE'>('MERGE');
  const [reason, setReason] = useState('');
  const [primary, setPrimary] = useState(phone);
  const [involved, setInvolved] = useState('');
  const [error, setError] = useState<string | null>(null);

  function close() {
    setAction('MERGE');
    setReason('');
    setPrimary(phone);
    setInvolved('');
    setError(null);
    onClose();
  }

  async function submit() {
    setError(null);
    if (!reason.trim()) {
      setError('Une justification est requise.');
      return;
    }
    const involvedList = involved
      .split(/[,\s]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    const result = await resolve.mutateAsync({
      action,
      reason,
      primaryPhone: action === 'MERGE' ? primary : undefined,
      involvedPhones: involvedList.length > 0 ? involvedList : undefined,
    });
    if (!result.ok) {
      setError(result.message);
      return;
    }
    close();
  }

  return (
    <Modal opened={opened} onClose={close} size={560} centered title="Arbitrer le conflit">
      <div style={{ marginBottom: 14, fontSize: 13.5, color: 'var(--ink-2)' }}>
        Pour <strong>{entrepreneurName}</strong> · <span style={{ fontFamily: 'var(--mono)' }}>{phone}</span>
      </div>

      <div className="field" style={{ marginBottom: 14 }}>
        <label>Décision</label>
        <div className="seg" style={{ width: '100%' }}>
          <button className={action === 'MERGE' ? 'on' : ''} style={{ flex: 1 }} onClick={() => setAction('MERGE')}>
            <Icon name="link" /> Fusionner les dossiers
          </button>
          <button
            className={action === 'KEEP_SEPARATE' ? 'on' : ''}
            style={{ flex: 1 }}
            onClick={() => setAction('KEEP_SEPARATE')}
          >
            <Icon name="x" /> Garder séparés
          </button>
        </div>
        <div className="hint">
          {action === 'MERGE'
            ? 'Les événements des dossiers liés seront rattachés au téléphone canonique.'
            : 'Les dossiers restent distincts. La marque CONFLIT disparaît.'}
        </div>
      </div>

      {action === 'MERGE' && (
        <div className="field" style={{ marginBottom: 14 }}>
          <label>Téléphone canonique</label>
          <input
            value={primary}
            onChange={(e) => setPrimary(e.target.value)}
            placeholder="+2250707000000"
            style={{ fontFamily: 'var(--mono)' }}
          />
        </div>
      )}

      <div className="field" style={{ marginBottom: 14 }}>
        <label>Autres téléphones concernés (séparés par des virgules)</label>
        <input
          value={involved}
          onChange={(e) => setInvolved(e.target.value)}
          placeholder="+2250707000001, +2250707000002"
          style={{ fontFamily: 'var(--mono)' }}
        />
      </div>

      <div className="field" style={{ marginBottom: 14 }}>
        <label>Justification <span style={{ color: 'var(--alert-ink)' }}>requise</span></label>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Ex: même commerçant, deux fiches PDE.CI divergentes par typo dans le téléphone."
        />
      </div>

      {error && <div className="err-msg" style={{ marginBottom: 14 }}>{error}</div>}

      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <Button variant="ghost" onClick={close} disabled={resolve.isPending}>Annuler</Button>
        <Button variant="primary" icon="check" onClick={submit} loading={resolve.isPending}>
          {action === 'MERGE' ? 'Confirmer la fusion' : 'Confirmer la séparation'}
        </Button>
      </div>
    </Modal>
  );
}
